import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { LFC } from '../../Legacy';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { Button, Input, usePrompt } from '../../ui';
import { ResultInfoForMutation } from '../../ui/ResultInfo';
import { useResolve } from '../Container';
import { Copy, Flexer, Spacer } from '../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from './Popup';

const useUpdateSeatsMutation = () => {
    const api = useResolve(ApiClient);

    return useMutation({
        mutationFn: async (seatCount: number) => {
            const result = await api.exec(v2.admin.billing.setQuantity({ quantity: seatCount }));
            return result;
        },
    });
};

export const newLegacyBillingQuantityPopup = (initialSeatCount: number) => ({ ui: LegacyBillingQuantityPopup, args: { initialSeatCount } });

type LegacyBillingQuantityPopupProps = PopupProps & { args: { initialSeatCount: number } };

export const LegacyBillingQuantityPopup: LFC<LegacyBillingQuantityPopupProps> = ({ action, dismiss, args }) => {
    const { initialSeatCount } = args;
    const mutation = useUpdateSeatsMutation();
    const [seatCount, setSeatCount] = useState(initialSeatCount);

    const invalid = seatCount <= 0;

    const onAction = (confirm: boolean) => {
        if (confirm) {
            if (!mutation.isLoading) {
                mutation.mutateAsync(seatCount)
                    .then(() => {
                        if (!mutation.isError) {
                            action(true);
                        }
                    });
            }
        }
        else {
            dismiss();
        }
    };

    const promptRef = usePrompt({
        onAction: mutation.isLoading ? null : onAction,
    });

    return (
        <Flexer ref={promptRef}>
            <Popup w={32}>
                <Popup.Close onAction={() => onAction(false)} />
                <Popup.Title>Update Seats</Popup.Title>
                <Popup.Content>
                    <Copy>Enter the desired number of seats for your plan:</Copy>
                    <Spacer f={1} />
                    <Input
                        label="Seat Count:"
                        placeholder="Enter seat count"
                        autoFocus
                        type="number"
                        value={seatCount.toString()}
                        onChange={(value) => setSeatCount(parseInt(value, 10))}
                    />
                    <Spacer />
                    <ResultInfoForMutation mutation={mutation} />
                </Popup.Content>
                <Popup.Actions>
                    <Button disabled={invalid || mutation.isLoading} variant="primary" onAction={() => onAction(true)}>
                        {mutation.isLoading ? 'Updating...' : 'Update Seats'}
                    </Button>
                    <Button disabled={mutation.isLoading} onAction={() => onAction(false)}>
                        Cancel
                    </Button>
                </Popup.Actions>
            </Popup>
        </Flexer>
    );
};
